// "Guests asked" manager — pick real questions from the inbox / chatbot, answer honestly, publish in 7 languages.
import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircleQuestion, Loader2, Plus, Trash2, Eye, EyeOff, Languages } from 'lucide-react';
import { apiCall } from './_shared';

const SRC = { contact: 'e-mail', chat: 'chat', manual: 'manual' };

const Candidate = ({ c, onPick }) => (
  <li className={`border rounded-lg p-3 flex flex-col sm:flex-row sm:items-start gap-2 ${c.flagged ? 'bg-amber-50 border-amber-200' : 'bg-white'}`} data-testid={`qa-candidate-${c.source_id}`}>
    <div className="flex-1 min-w-0">
      {c.flagged && <p className="text-[10px] uppercase tracking-wide font-bold text-amber-700 mb-0.5" data-testid={`qa-flag-${c.source_id}`}>Guest said “not answered”{c.count > 1 ? ` × ${c.count}` : ''}</p>}
      <p className="text-sm text-slate-900">{c.text}</p>
      {c.flagged && c.bot_response && <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">Bot replied: {c.bot_response}</p>}
      <p className="text-[11px] text-slate-400 mt-1">{SRC[c.source]} · {String(c.at || '').slice(0, 10)}{c.suggested_answer ? ' · has a reply to start from' : ''}</p>
    </div>
    <button type="button" onClick={() => onPick(c)} className="inline-flex items-center gap-1 border rounded px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex-shrink-0" data-testid={`qa-pick-${c.source_id}`}><Plus className="w-3 h-3" /> Answer</button>
  </li>
);

const PublishedRow = ({ q, rooms, busy, onToggle, onDelete, canDelete }) => {
  const langs = Object.keys(q.translations || {}).length;
  const room = rooms.find((r) => r.id === q.room_id);
  return (
    <li className={`border rounded-lg p-3 ${q.published ? 'bg-white' : 'bg-slate-50 opacity-80'}`} data-testid={`qa-row-${q.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{q.question}</p>
          <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{q.answer}</p>
          <p className="text-[11px] text-slate-400 mt-1"><Languages className="w-3 h-3 inline" /> {langs}/7 languages · {SRC[q.source]} · {room ? room.name : 'all rooms'} · {q.published ? 'published' : 'hidden'}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={() => onToggle(q)} disabled={busy} className="inline-flex items-center gap-1 border rounded px-2 py-1 text-xs text-slate-700 hover:bg-slate-50" data-testid={`qa-toggle-${q.id}`}>{q.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {q.published ? 'Hide' : 'Publish'}</button>
          {canDelete && <button type="button" onClick={() => onDelete(q)} disabled={busy} className="inline-flex items-center border rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" data-testid={`qa-delete-${q.id}`}><Trash2 className="w-3 h-3" /></button>}
        </div>
      </div>
    </li>
  );
};

const GuestQaManager = ({ userRole }) => {
  const [cands, setCands] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const canDelete = ['super_admin', 'admin', 'manager'].includes(userRole);

  const load = useCallback(async () => {
    try {
      const [c, i, r] = await Promise.all([apiCall('/api/admin/guest-qa/candidates'), apiCall('/api/admin/guest-qa'), fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms`).then((x) => x.json())]);
      setCands(c.items || []); setItems(i.items || []); setRooms(Array.isArray(r) ? r : []);
    } catch (e) { setMsg(String(e.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = (c) => setDraft({ question: c.text, answer: c.suggested_answer || '', room_id: '', source: c.source, source_id: c.source_id, asked_at: c.at, published: true });
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const body = { ...draft, room_id: draft.room_id === '' ? null : Number(draft.room_id) };
      await apiCall('/api/admin/guest-qa', { method: 'POST', body: JSON.stringify(body) });
      setDraft(null); setMsg('Published in 7 languages.'); await load();
    } catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };
  const toggle = async (q) => {
    setBusy(true);
    try { await apiCall(`/api/admin/guest-qa/${q.id}`, { method: 'PUT', body: JSON.stringify({ published: !q.published }) }); await load(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };
  const del = async (q) => {
    if (!window.confirm('Delete this Q&A?')) return;
    setBusy(true);
    try { await apiCall(`/api/admin/guest-qa/${q.id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };

  const candRows = cands.map((c) => <Candidate key={c.source_id} c={c} onPick={pick} />);
  const pubRows = items.map((q) => <PublishedRow key={q.id} q={q} rooms={rooms} busy={busy} onToggle={toggle} onDelete={del} canDelete={canDelete} />);
  const roomOpts = rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>);

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="guest-qa-manager">
      <div>
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><MessageCircleQuestion className="w-5 h-5 text-orange-500" /> Guests asked — public Q&A</h3>
        <p className="text-xs text-slate-500 mt-1">Real questions from the contact inbox and the website chat. Write a short, honest answer; it is translated into 7 languages and shown on the home page, the room page you choose and /trust. Personal details are stripped automatically.</p>
      </div>
      {msg && <p className="text-xs text-slate-700 bg-slate-50 border rounded px-3 py-2" data-testid="qa-msg">{msg}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setDraft({ question: '', answer: '', room_id: '', source: 'manual', source_id: null, asked_at: null, published: true })} className="inline-flex items-center gap-1 border rounded px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="qa-new-manual"><Plus className="w-3 h-3" /> Add a question asked at reception</button>
        <span className="text-xs text-slate-400">{cands.length} candidate questions · {items.filter((x) => x.published).length} published</span>
      </div>

      {draft && (
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-3 space-y-2" data-testid="qa-editor">
          <label className="block text-xs font-semibold text-slate-700">Question (as the guest asked it, personal details removed)
            <input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} className="block w-full border rounded px-2 py-1.5 mt-1 text-sm font-normal" data-testid="qa-question" /></label>
          <label className="block text-xs font-semibold text-slate-700">Honest answer (facts only — no superlatives)
            <textarea value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={4} className="block w-full border rounded px-2 py-1.5 mt-1 text-sm font-normal" data-testid="qa-answer" /></label>
          <div className="flex flex-wrap items-center gap-2">
            <select value={draft.room_id} onChange={(e) => setDraft({ ...draft, room_id: e.target.value })} className="border rounded px-2 py-1.5 text-xs" data-testid="qa-room"><option value="">Show on all pages</option>{roomOpts}</select>
            <button type="button" onClick={save} disabled={busy || draft.question.trim().length < 5 || draft.answer.trim().length < 5} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-orange-600 disabled:opacity-60" data-testid="qa-save">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} Translate & publish</button>
            <button type="button" onClick={() => setDraft(null)} className="text-xs text-slate-500 hover:underline" data-testid="qa-cancel">Cancel</button>
          </div>
        </div>
      )}

      {candRows.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Questions guests actually asked (last 120 days)</p>
          <ul className="space-y-2" data-testid="qa-candidates">{candRows}</ul>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-slate-700 mb-2">Published Q&A</p>
        {pubRows.length ? <ul className="space-y-2" data-testid="qa-published">{pubRows}</ul> : <p className="text-xs text-slate-400">Nothing published yet — pick a question above.</p>}
      </div>
    </div>
  );
};

export default GuestQaManager;
